import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { User } from '../types';
import * as authApi from '../api/auth';
import * as guestApi from '../api/guest';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isGuest: boolean;
  isLoading: boolean;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  /** Resolves with the pages a page-bound passcode opens (empty for a dashboard passcode). */
  loginAsGuest: (passcode: string) => Promise<{ slug: string; title: string }[]>;
  register: (name: string, email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  hasPermission: (permission: string) => boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  isGuest: false,
  isLoading: true,
  login: async () => {},
  loginAsGuest: async () => [],
  register: async () => {},
  logout: async () => {},
  refreshUser: async () => {},
  hasPermission: () => false,
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    try {
      const { user } = await authApi.getMe();
      setUser(user);
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    authApi.getMe()
      .then(({ user }) => setUser(user))
      .catch(() => setUser(null))
      .finally(() => setIsLoading(false));
  }, []);

  const login = useCallback(async (email: string, password: string, rememberMe?: boolean) => {
    const { user } = await authApi.login({ email, password, rememberMe });
    setUser(user);
  }, []);

  const loginAsGuest = useCallback(async (passcode: string) => {
    const { user, pages, page_scoped } = await guestApi.verifyPasscode(passcode);
    // A page-bound passcode is not a dashboard session: leave the app signed
    // out and let the caller send the visitor to the page.
    if (page_scoped) return pages ?? [];
    setUser(user);
    return [];
  }, []);

  const register = useCallback(async (name: string, email: string, password: string) => {
    await authApi.register({ email, password, name });
  }, []);

  const logout = useCallback(async () => {
    try {
      if (user?.isGuest) {
        await guestApi.guestLogout();
      } else {
        await authApi.logout();
      }
    } finally {
      setUser(null);
    }
  }, [user]);

  const hasPermission = useCallback((permission: string) => {
    if (!user?.permissions) return false;
    if (user.permissions.includes('*')) return true;
    return user.permissions.includes(permission);
  }, [user]);

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated: !!user,
      isGuest: !!user?.isGuest,
      isLoading,
      login,
      loginAsGuest,
      register,
      logout,
      refreshUser,
      hasPermission,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
